// Dialog khách chấm điểm ê-kíp (spec 2026-08-20 §5.1, mockup A đã duyệt):
// 1 màn dọc — mỗi người 1 khối tên + ảnh + hàng 5 bông hoa; lễ tân + quản
// lý cửa hàng khách tự chọn từ danh sách có ảnh; điểm ≤2 bung chips lý do;
// nhận xét chung; sửa thoải mái tới khi tháng phiếu chốt lương (khoá).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Coffee, Loader2, LockKeyhole } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/ui/avatar";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/ui/dialog";
import { Textarea } from "@/ui/textarea";
import { FlowerRating, SCORE_LABELS } from "./FlowerRating";
import {
  fetchReviewContext,
  reviewDeviceId,
  submitReview,
  type ReviewContext,
  type ReviewRosterPerson,
} from "./review-api";

/** Chips lý do khi chấm 1–2 điểm (BE bắt buộc ≥1 khi điểm thấp). */
const LOW_SCORE_TAGS = [
  "Thái độ chưa tốt",
  "Chờ lâu / trễ hẹn",
  "Chất lượng ảnh",
  "Trang điểm / làm tóc",
  "Chỉnh sửa ảnh",
  "Giao ảnh chậm",
  "Tư vấn không rõ ràng",
  "Khác",
];

type EntryDraft = {
  score: number;
  reasonTags: string[];
  reasonNote: string;
};

type Drafts = Record<string, EntryDraft>;

function draftOf(drafts: Drafts, staffId: string): EntryDraft {
  return drafts[staffId] ?? { score: 0, reasonTags: [], reasonNote: "" };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const last = parts[parts.length - 1] ?? "";
  return last.slice(0, 2).toUpperCase() || "GN";
}

function PersonRow({
  person,
  draft,
  locked,
  onChange,
}: {
  person: ReviewRosterPerson;
  draft: EntryDraft;
  locked: boolean;
  onChange: (next: EntryDraft) => void;
}) {
  const low = draft.score > 0 && draft.score <= 2;
  return (
    <div className="rounded-2xl border bg-surface-2/50 p-3">
      <div className="flex items-center gap-3">
        <Avatar className="h-11 w-11">
          {person.avatarUrl ? <AvatarImage src={person.avatarUrl} alt={person.name} /> : null}
          <AvatarFallback>{initials(person.name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold">{person.name}</div>
          <div className="text-xs text-muted-foreground">{person.roleLabel}</div>
        </div>
        {draft.score > 0 && (
          <span className="shrink-0 text-xs font-medium text-muted-foreground">{SCORE_LABELS[draft.score]}</span>
        )}
      </div>
      <div className={locked ? "pointer-events-none mt-2 opacity-70" : "mt-2"}>
        <FlowerRating score={draft.score} onChange={(score) => onChange({ ...draft, score })} />
      </div>
      {low && (
        <div className="mt-2">
          <div className="text-xs font-medium text-danger-text">Điều gì khiến bạn chưa hài lòng? (chọn ít nhất 1)</div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {LOW_SCORE_TAGS.map((tag) => {
              const active = draft.reasonTags.includes(tag);
              return (
                <button
                  key={tag}
                  type="button"
                  disabled={locked}
                  onClick={() =>
                    onChange({
                      ...draft,
                      reasonTags: active ? draft.reasonTags.filter((t) => t !== tag) : [...draft.reasonTags, tag],
                    })
                  }
                  className={
                    "min-h-9 rounded-full border px-3 text-xs font-medium transition-colors " +
                    (active ? "border-danger-text bg-danger-text text-white" : "bg-surface text-muted-foreground")
                  }
                >
                  {tag}
                </button>
              );
            })}
          </div>
          <Textarea
            value={draft.reasonNote}
            disabled={locked}
            onChange={(e) => onChange({ ...draft, reasonNote: e.target.value })}
            placeholder="Chia sẻ thêm để studio khắc phục (không bắt buộc)"
            className="mt-2 min-h-16 text-sm"
            maxLength={500}
          />
        </div>
      )}
    </div>
  );
}

function PickerSection({
  title,
  people,
  picked,
  locked,
  onPick,
}: {
  title: string;
  people: ReviewRosterPerson[];
  picked: string[];
  locked: boolean;
  onPick: (id: string) => void;
}) {
  if (people.length === 0) return null;
  return (
    <div>
      <div className="text-sm font-semibold">{title}</div>
      <p className="text-xs text-muted-foreground">Bạn nhớ ai đã hỗ trợ mình thì chọn để chấm — không nhớ có thể bỏ qua.</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {people.map((p) => {
          const active = picked.includes(p.staffId);
          return (
            <button
              key={p.staffId}
              type="button"
              disabled={locked}
              onClick={() => onPick(p.staffId)}
              className={
                "flex min-h-11 items-center gap-2 rounded-xl border px-3 py-1.5 text-sm transition-colors " +
                (active ? "border-[var(--color-brand)] bg-[var(--color-brand)]/10 font-semibold" : "bg-surface")
              }
            >
              <Avatar className="h-8 w-8">
                {p.avatarUrl ? <AvatarImage src={p.avatarUrl} alt={p.name} /> : null}
                <AvatarFallback className="text-xs">{initials(p.name)}</AvatarFallback>
              </Avatar>
              <span className="max-w-32 truncate">{p.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ReviewEkipDialog({
  orderId,
  open,
  onOpenChange,
  onInviteTip,
}: {
  orderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Sau khi lưu xong, khách bấm "Mời ê-kíp ly cà phê" ở màn cảm ơn. */
  onInviteTip?: (() => void) | undefined;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-lg">
        <ReviewEkipDialogBody
          key={orderId}
          orderId={orderId}
          onOpenChange={onOpenChange}
          onInviteTip={onInviteTip}
        />
      </DialogContent>
    </Dialog>
  );
}

// Thân nằm BÊN TRONG DialogContent nên Radix tự tháo khi hộp đóng: mỗi lần mở
// là một lượt mới, state khai báo sẵn đúng giá trị đầu — khỏi phải reset bằng
// effect (reset đồng bộ trong effect đẻ ra một lượt vẽ thừa). key theo mã đơn
// để đổi đơn giữa chừng cũng làm mới đúng như vậy.
function ReviewEkipDialogBody({
  orderId,
  onOpenChange,
  onInviteTip,
}: {
  orderId: string;
  onOpenChange: (open: boolean) => void;
  onInviteTip?: (() => void) | undefined;
}) {
  const [ctx, setCtx] = useState<ReviewContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [drafts, setDrafts] = useState<Drafts>({});
  const [picked, setPicked] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [wish, setWish] = useState("");
  const [thanks, setThanks] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchReviewContext(orderId, { signal: controller.signal })
      .then((data) => {
        setCtx(data);
        setComment(data.review?.comment ?? "");
        setWish(data.review?.wish ?? "");
        const next: Drafts = {};
        const pickedIds: string[] = [];
        const rosterIds = new Set(data.roster.map((p) => p.staffId));
        for (const entry of data.review?.entries ?? []) {
          next[entry.staffId] = {
            score: entry.score,
            reasonTags: entry.reasonTags ?? [],
            reasonNote: entry.reasonNote ?? "",
          };
          if (!rosterIds.has(entry.staffId)) pickedIds.push(entry.staffId);
        }
        setDrafts(next);
        setPicked(pickedIds);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Không tải được thông tin chấm điểm");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [orderId]);

  const locked = ctx?.locked ?? false;

  const pickerPeople = useMemo(() => {
    if (!ctx) return new Map<string, ReviewRosterPerson>();
    const map = new Map<string, ReviewRosterPerson>();
    for (const p of [...ctx.pickers.reception, ...ctx.pickers.storeManagers]) map.set(p.staffId, p);
    return map;
  }, [ctx]);

  const setDraft = useCallback((staffId: string, next: EntryDraft) => {
    setDrafts((prev) => ({ ...prev, [staffId]: next }));
  }, []);

  const togglePick = useCallback((id: string) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const scoredCount = useMemo(
    () => Object.values(drafts).filter((d) => d.score > 0).length,
    [drafts],
  );

  const lowMissingReason = useMemo(
    () => Object.values(drafts).some((d) => d.score > 0 && d.score <= 2 && d.reasonTags.length === 0),
    [drafts],
  );

  async function handleSubmit() {
    if (!ctx) return;
    const entries = Object.entries(drafts)
      .filter(([, d]) => d.score > 0)
      .map(([staffId, d]) => ({
        staffId,
        score: d.score,
        reasonTags: d.score <= 2 ? d.reasonTags : [],
        reasonNote: d.score <= 2 ? d.reasonNote.trim() : "",
      }));
    if (entries.length === 0) {
      toast.error("Bạn chưa chấm ai — chọn ít nhất một người nhé");
      return;
    }
    if (lowMissingReason) {
      toast.error("Điểm 1–2 cần chọn ít nhất một lý do để studio khắc phục");
      return;
    }
    setSaving(true);
    try {
      await submitReview(orderId, {
        deviceId: reviewDeviceId(),
        comment: comment.trim(),
        wish: wish.trim(),
        entries,
      });
      setThanks(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Không gửi được đánh giá");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {thanks ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <div className="text-4xl">🌸</div>
          <DialogTitle>Cảm ơn bạn đã chấm điểm!</DialogTitle>
          <DialogDescription>
            Đánh giá của bạn được gửi tới từng thành viên ê-kíp và ảnh hưởng trực tiếp tới thưởng của họ.
          </DialogDescription>
          {onInviteTip && (
            <Button
              className="mt-2 min-h-11 w-full"
              onClick={() => {
                onOpenChange(false);
                onInviteTip();
              }}
            >
              <Coffee className="mr-2 h-4 w-4" />
              Mời ê-kíp ly cà phê
            </Button>
          )}
          <Button variant="outline" className="min-h-11 w-full" onClick={() => onOpenChange(false)}>
            Đóng
          </Button>
        </div>
      ) : (
        <>
          <DialogHeader>
            <DialogTitle>Chấm điểm ê-kíp {ctx ? `— đơn #${ctx.orderCode}` : ""}</DialogTitle>
            <DialogDescription>
              Mỗi người một hàng hoa: 1 là rất tệ, 5 là rất hài lòng. Ai bạn không nhớ có thể bỏ trống.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Đang tải…
            </div>
          )}
          {!loading && error && <p className="py-4 text-sm text-danger-text">{error}</p>}

          {!loading && !error && ctx && (
            <div className="grid gap-3">
              {locked && (
                <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                  <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0" />
                  Kỳ lương của phiếu này đã chốt nên đánh giá được khoá lại — bạn vẫn xem được điểm đã chấm.
                </div>
              )}

              {ctx.roster.map((person) => (
                <PersonRow
                  key={person.staffId}
                  person={person}
                  locked={locked}
                  draft={draftOf(drafts, person.staffId)}
                  onChange={(next) => setDraft(person.staffId, next)}
                />
              ))}

              <PickerSection
                title="Bạn lễ tân đón bạn hôm đó"
                people={ctx.pickers.reception}
                picked={picked}
                locked={locked}
                onPick={togglePick}
              />
              <PickerSection
                title="Quản lý cửa hàng"
                people={ctx.pickers.storeManagers}
                picked={picked}
                locked={locked}
                onPick={togglePick}
              />
              {picked.map((id) => {
                const person = pickerPeople.get(id);
                if (!person) return null;
                return (
                  <PersonRow
                    key={id}
                    person={person}
                    locked={locked}
                    draft={draftOf(drafts, id)}
                    onChange={(next) => setDraft(id, next)}
                  />
                );
              })}

              <div>
                <div className="text-sm font-semibold">Nhận xét chung (không bắt buộc)</div>
                <Textarea
                  value={comment}
                  disabled={locked}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Cảm nhận của bạn về buổi chụp…"
                  className="mt-1.5 min-h-20"
                  maxLength={2000}
                />
              </div>

              {/* Khảo sát "khách mong gì" — nguồn duy nhất của khu Mong muốn ở
                  tab Phân tích kinh doanh. Hỏi TÁCH khỏi ô nhận xét vì đây là
                  câu hướng về TƯƠNG LAI: gộp vào "cảm nhận buổi chụp" thì
                  khách chỉ kể lại buổi vừa rồi, không nói mong muốn lần sau.
                  Luôn không bắt buộc: chặn nút gửi vì ô này là đánh đổi một
                  câu khảo sát lấy cả phiếu chấm điểm ê-kíp. */}
              <div>
                <div className="text-sm font-semibold">Lần sau bạn mong muốn điều gì? (không bắt buộc)</div>
                <Textarea
                  value={wish}
                  disabled={locked}
                  onChange={(e) => setWish(e.target.value)}
                  placeholder="Concept mới, thêm trang phục, giờ chụp khác…"
                  className="mt-1.5 min-h-20"
                  maxLength={500}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Studio đọc để chuẩn bị cho lần chụp tới của bạn.
                </p>
              </div>

              {!locked && (
                <>
                  <Button className="min-h-12 w-full" onClick={handleSubmit} disabled={saving}>
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {ctx.review ? "Cập nhật đánh giá" : `Gửi đánh giá${scoredCount > 0 ? ` (${scoredCount} người)` : ""}`}
                  </Button>
                  <p className="text-center text-xs text-muted-foreground">
                    Bạn sửa được đánh giá tới khi kỳ lương tháng này chốt sổ.
                  </p>
                </>
              )}
            </div>
          )}
        </>
      )}
    </>
  );
}
